/**
 * Cobalt helpers — currency, dates, semantic color mapping for status / stage /
 * priority. Pure utilities; no React imports.
 */

import { formatDistanceToNow } from 'date-fns';

// ── Currency / numbers ──────────────────────────────────────────────

export function fmtCurrency(n: number | null | undefined, opts?: { compact?: boolean }): string {
  if (n == null) return '—';
  if (opts?.compact) {
    if (n >= 1_000_000) return '$' + (n / 1_000_000).toFixed(n >= 10_000_000 ? 0 : 1) + 'M';
    if (n >= 1_000)     return '$' + (n / 1_000).toFixed(n >= 100_000 ? 0 : 1) + 'k';
    return '$' + n;
  }
  return new Intl.NumberFormat('en-AU', {
    style: 'currency',
    currency: 'AUD',
    maximumFractionDigits: 0,
  }).format(n);
}

// ── Dates ───────────────────────────────────────────────────────────

export function fmtDate(d: string | null | undefined): string {
  if (!d) return '—';
  try {
    return new Date(d).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' });
  } catch {
    return '—';
  }
}

export function fmtRelative(d: string | null | undefined): string {
  if (!d) return '—';
  try {
    return formatDistanceToNow(new Date(d), { addSuffix: true });
  } catch {
    return '—';
  }
}

/** "Overdue 2h" / "Due today" / "Due Fri" / null — drives `DueChip`. */
export type DueState = 'overdue' | 'soon' | 'normal' | 'none';
export function dueState(d: string | null | undefined): DueState {
  if (!d) return 'none';
  const ms = new Date(d).getTime();
  if (Number.isNaN(ms)) return 'none';
  const delta = ms - Date.now();
  if (delta < 0) return 'overdue';
  if (delta < 48 * 60 * 60 * 1000) return 'soon';
  return 'normal';
}

// ── Status / priority / stage → semantic kind ───────────────────────

export type BadgeKind = 'brand' | 'info' | 'success' | 'warning' | 'destructive' | 'neutral';

/** Map an Autotask status LABEL (not id) to a semantic chip kind. */
export function statusKind(label: string | null | undefined): BadgeKind {
  if (!label) return 'neutral';
  const l = label.toLowerCase();
  if (l.startsWith('new'))                 return 'info';
  if (l.includes('in progress'))           return 'brand';
  if (l.includes('waiting customer'))      return 'warning';
  if (l.includes('waiting'))               return 'warning';
  if (l.includes('escalat'))               return 'destructive';
  if (l.includes('scheduled'))             return 'neutral';
  if (l.includes('complete') || l.includes('closed') || l.includes('resolved')) return 'success';
  return 'neutral';
}

/**
 * Per-stage distinct color for pipeline visualisations (bar, legend dots).
 * Returns a CSS hex string. Each Autotask stage gets a unique hue so segments
 * are easy to tell apart even when many stages are active at once.
 */
export function stageColor(label: string | null | undefined): string {
  if (!label) return '#6b7280';
  const l = label.toLowerCase();
  if (l.includes('new lead'))                              return '#f59e0b'; // amber
  if (l.includes('stage 1') || l.includes('opportunity')) return '#06b6d4'; // cyan
  if (l.includes('stage 2') || l.includes('presale'))     return '#3b82f6'; // blue
  if (l.includes('stage 3') || l.includes('quoting'))     return '#6366f1'; // indigo
  if (l.includes('stage 4') || l.includes('quote sent'))  return '#8b5cf6'; // violet
  if (l.includes('stage 5') || l.includes('negotiat'))    return '#f97316'; // orange
  if (l.includes('stage 6') || l.includes('accept') || l.includes('won'))  return '#22c55e'; // green
  if (l.includes('stage 7') || l.includes('declin') || l.includes('lost')) return '#ef4444'; // red
  return '#6b7280'; // neutral fallback
}

/** Map an Autotask stage label to a semantic chip kind. */
export function stageKind(label: string | null | undefined): BadgeKind {
  if (!label) return 'neutral';
  const l = label.toLowerCase();
  if (l.includes('new lead'))                            return 'warning';
  if (l.includes('stage 1') || l.includes('opportunity')) return 'info';
  if (l.includes('stage 2') || l.includes('presale'))    return 'brand';
  if (l.includes('stage 3') || l.includes('quoting'))    return 'brand';
  if (l.includes('stage 4') || l.includes('quote sent')) return 'info';
  if (l.includes('stage 5') || l.includes('negotiat'))   return 'warning';
  if (l.includes('stage 6') || l.includes('accept') || l.includes('won')) return 'success';
  if (l.includes('stage 7') || l.includes('declin') || l.includes('lost')) return 'destructive';
  return 'neutral';
}

/** Priority labels → semantic kind. Drives PriorityTag's color. */
export function priorityKind(label: string | null | undefined): 'critical' | 'high' | 'medium' | 'low' {
  if (!label) return 'low';
  const l = label.toLowerCase();
  if (l.includes('critical') || l === '1') return 'critical';
  if (l.includes('high')     || l === '2') return 'high';
  if (l.includes('medium')   || l === '3') return 'medium';
  return 'low';
}

// ── Initials / mark helpers ─────────────────────────────────────────

/** "Acme Industries" → "AI",  "Lisa Chen" → "LC", "Synfo" → "Sy". */
export function initials(name: string | null | undefined, max = 2): string {
  if (!name) return '?';
  const words = name.trim().split(/\s+/).filter(Boolean);
  if (words.length === 0) return '?';
  if (words.length === 1) return words[0].slice(0, 2).toUpperCase();
  return (words[0][0] + words[words.length - 1][0]).toUpperCase().slice(0, max);
}

/**
 * Stable per-name accent color. Returns a CSS color string drawn from a small
 * curated palette (always works on both light + dark surfaces).
 *
 * Pass the SAME name for the SAME entity and you get the SAME color across the
 * app — so 'Acme Industries' is always the same shade everywhere.
 */
const MARK_PALETTE = [
  '14 99 156',   // info blue
  '155 91 24',   // warm amber
  '14 138 95',   // green
  '197 54 59',   // red
  '116 73 178',  // violet
  '15 113 132',  // teal
  '180 78 31',   // copper
  '95 99 117',   // slate
];

export function markColor(name: string | null | undefined): string {
  if (!name) return MARK_PALETTE[0];
  let h = 0;
  for (let i = 0; i < name.length; i++) h = (h * 31 + name.charCodeAt(i)) >>> 0;
  return MARK_PALETTE[h % MARK_PALETTE.length];
}
